import React, { useEffect, useMemo, useState } from "react"
import { Card, CardBody, Row, Col, Input, Button } from "reactstrap"
import ReactHTMLTableToExcel from "react-html-table-to-excel"
import moment from "moment"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import TableLoader from "components/Common/TableLoader"
import { getBonusAwardsCall } from "helpers/repository"
import { mapBonusAwardApiResponse, AUDIT_STATUS_META } from "./data"
import { formatDate } from "./utils"
import AuditDrawer from "./AuditDrawer"
import StatusBadge from "./StatusBadge"
import { useCanManageAffiliateBonuses } from "./permissions"

const PAGE_SIZE = 8
const GROUP_SIZE = 3
// GET /admin/bonus-awards paginates server-side, but there's no confirmed way
// to push search/campaign/status/date filtering down to the API, and the
// "Failed" filter here covers two distinct backend statuses (rejected,
// expired). So every page is pulled up front and filtering stays client-side,
// same as the UI that used to run on mock data.
const API_PAGE_SIZE = 100

const AuditLogs = () => {
  const canEdit = useCanManageAffiliateBonuses()
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [search, setSearch] = useState("")
  const [campaignFilter, setCampaignFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [dateFilter, setDateFilter] = useState("")
  const [activeRow, setActiveRow] = useState(null)
  const [page, setPage] = useState(1)

  useEffect(() => {
    let cancelled = false

    const fetchAllAwards = async () => {
      const first = await getBonusAwardsCall({ page: 1, per_page: API_PAGE_SIZE })
      const firstPage = first?.data?.data ?? {}
      const lastPage = Number(firstPage.last_page) || 1
      let raw = firstPage.data ?? []

      if (lastPage > 1) {
        const rest = await Promise.all(
          Array.from({ length: lastPage - 1 }, (_, i) =>
            getBonusAwardsCall({ page: i + 2, per_page: API_PAGE_SIZE })
          )
        )
        rest.forEach(res => {
          raw = raw.concat(res?.data?.data?.data ?? [])
        })
      }
      return raw
    }

    setLoading(true)
    fetchAllAwards()
      .then(raw => {
        if (cancelled) return
        setRows(raw.map(mapBonusAwardApiResponse))
      })
      .catch(err => {
        if (cancelled) return
        console.error(err)
        toastr.error("Could not load bonus award audit records.")
        setRows([])
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const campaignOptions = useMemo(() => [...new Set(rows.map(r => r.campaign))].sort(), [rows])

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter(a => {
      const matchesSearch =
        !q ||
        a.player.toLowerCase().includes(q) ||
        a.username.toLowerCase().includes(q) ||
        a.affiliate.toLowerCase().includes(q)
      const matchesCampaign = !campaignFilter || a.campaign === campaignFilter
      const matchesStatus = !statusFilter || a.status === statusFilter
      const matchesDate = !dateFilter || a.awardDate.slice(0, 10) === dateFilter
      return matchesSearch && matchesCampaign && matchesStatus && matchesDate
    })
  }, [rows, search, campaignFilter, statusFilter, dateFilter])

  useEffect(() => {
    setPage(1)
  }, [search, campaignFilter, statusFilter, dateFilter])

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pagedRows = filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const groupStart = Math.floor((currentPage - 1) / GROUP_SIZE) * GROUP_SIZE + 1
  const pageSlots = Array.from({ length: GROUP_SIZE }, (_, i) => groupStart + i).filter(
    n => n <= totalPages
  )

  const hasFilters = search.trim() !== "" || campaignFilter !== "" || statusFilter !== "" || dateFilter !== ""

  // There's no confirmed "retry a rejected award" endpoint yet — this only
  // updates the row locally so the UI reflects the retry immediately.
  const handleRetry = () => {
    if (!activeRow) return
    setRows(prev => prev.map(a => (a.id === activeRow.id ? { ...a, status: "Credited", reason: "" } : a)))
    setActiveRow(null)
  }

  return (
    <Card className="ab-card">
      <style>{`
        .ab-card { border-radius: 10px; overflow: hidden; }

        .affiliate-bonuses-pagination .pagination { gap: 6px; }
        .affiliate-bonuses-pagination .page-item .page-link {
          border: 1px solid #e2e5ec;
          border-radius: 8px;
          min-width: 32px;
          height: 32px;
          margin: 0;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #495057;
          background-color: #fff;
          box-shadow: none;
        }
        .affiliate-bonuses-pagination .page-item:not(.active):not(.disabled) .page-link:hover {
          border-color: #556ee6;
          color: #556ee6;
        }
        .affiliate-bonuses-pagination .page-item.active .page-link {
          border-color: #556ee6;
          background-color: #556ee6;
          color: #fff;
        }
        .affiliate-bonuses-pagination .page-item.disabled .page-link {
          color: #adb5bd;
          background-color: #fff;
        }

        .ab-search-box { position: relative; }
        .ab-search-box .form-control { border-radius: 4px; padding-left: 34px; }
        .ab-search-box .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 14px;
          line-height: 1;
          color: #adb5bd;
        }

        .ab-audit-table tbody tr:hover td {
          background-color: rgba(85, 110, 230, 0.06);
        }
      `}</style>
      <CardBody>
        <div className="d-sm-flex align-items-center justify-content-between mb-3">
          <div className="h4 card-title mb-0">Bonus Award Audit</div>
          <ReactHTMLTableToExcel
            id="audit-export-button"
            className="download-table-xls-button btn btn-outline-secondary btn-sm"
            table="audit-table-to-xls"
            filename={`Affiliate-Bonus-Audit-${moment(new Date()).format("DD-MM-YYYY")}`}
            sheet="audit"
            buttonText="Export CSV"
          />
        </div>

        <Row className="mb-3 g-2">
          <Col sm={4}>
            <div className="ab-search-box w-100">
              <Input
                type="text"
                placeholder="Search player, username or affiliate..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <i className="bx bx-search-alt search-icon" />
            </div>
          </Col>
          <Col sm={3}>
            <Input type="select" value={campaignFilter} onChange={e => setCampaignFilter(e.target.value)}>
              <option value="">All Campaigns</option>
              {campaignOptions.map(name => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Input>
          </Col>
          <Col sm={3}>
            <Input type="select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="Credited">Credited</option>
              <option value="Pending">Pending</option>
              <option value="Failed">Failed</option>
            </Input>
          </Col>
          <Col sm={2}>
            <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} />
          </Col>
        </Row>

        {loading ? (
          <TableLoader />
        ) : filteredRows.length === 0 ? (
          <div className="text-center py-5">
            <div
              className="rounded-circle bg-primary bg-opacity-10 d-inline-flex align-items-center justify-content-center mb-3"
              style={{ width: 64, height: 64 }}
            >
              <i className="mdi mdi-file-document-multiple-outline text-primary" style={{ fontSize: "26px" }} />
            </div>
            <h5 className="mb-2">{hasFilters ? "No matching audit records" : "No audit records"}</h5>
            <p className="text-muted mx-auto mb-0" style={{ maxWidth: 340 }}>
              {hasFilters
                ? "Try adjusting your search or filters."
                : "Bonus award events will appear here once players register through an affiliate link."}
            </p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table align-middle table-nowrap mb-0 ab-audit-table">
                <thead className="table-light">
                  <tr>
                    <th>Player</th>
                    <th>Affiliate</th>
                    <th>Campaign</th>
                    <th className="text-end">Amount</th>
                    <th>Award Date</th>
                    <th>Status</th>
                    <th>Failure Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map(a => (
                    <tr key={a.id} style={{ cursor: "pointer" }} onClick={() => setActiveRow(a)}>
                      <td className="fw-bold">{a.player}</td>
                      <td className="text-muted">{a.affiliate}</td>
                      <td className="text-muted">{a.campaign}</td>
                      <td className="text-end fw-bold">{a.amount}</td>
                      <td className="text-muted">{formatDate(a.awardDate)}</td>
                      <td>
                        <StatusBadge status={a.status} meta={AUDIT_STATUS_META} />
                      </td>
                      <td className="text-muted">{a.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Row className="mt-3 align-items-center">
              <Col sm="6">
                <div className="text-muted">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, filteredRows.length)}{" "}
                  of {filteredRows.length} audit events
                </div>
              </Col>
              <Col sm="6">
                <ul className="pagination affiliate-bonuses-pagination justify-content-end mb-0">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button
                      type="button"
                      className="page-link"
                      disabled={currentPage === 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                      ‹
                    </button>
                  </li>
                  {pageSlots.map(n => {
                    const disabled = n > totalPages
                    return (
                      <li key={n} className={`page-item ${n === currentPage ? "active" : ""} ${disabled ? "disabled" : ""}`}>
                        <button
                          type="button"
                          className="page-link"
                          disabled={disabled}
                          aria-current={n === currentPage ? "page" : undefined}
                          aria-label={`Page ${n}`}
                          onClick={() => setPage(n)}
                        >
                          {n}
                        </button>
                      </li>
                    )
                  })}
                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button
                      type="button"
                      className="page-link"
                      disabled={currentPage === totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    >
                      ›
                    </button>
                  </li>
                </ul>
              </Col>
            </Row>
          </>
        )}

        {/* Full filtered dataset, kept off-screen so CSV export reflects all matching
            rows regardless of the current page. */}
        <table id="audit-table-to-xls" className="table d-none">
          <thead>
            <tr>
              <th>Player</th>
              <th>Affiliate</th>
              <th>Campaign</th>
              <th>Amount</th>
              <th>Award Date</th>
              <th>Status</th>
              <th>Failure Reason</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map(a => (
              <tr key={a.id}>
                <td>{a.player}</td>
                <td>{a.affiliate}</td>
                <td>{a.campaign}</td>
                <td>{a.amount}</td>
                <td>{formatDate(a.awardDate)}</td>
                <td>{a.status}</td>
                <td>{a.reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
      <AuditDrawer
        open={!!activeRow}
        row={activeRow}
        onClose={() => setActiveRow(null)}
        onRetry={handleRetry}
        canEdit={canEdit}
      />
    </Card>
  )
}

export default AuditLogs
